// Grouped client for Main operations
import { ApiConfiguration } from '../ApiConfiguration';
import { Form } from '../models/Form';
import { DataFormActionRequest } from '../models/DataFormActionRequest';
import { DataFormActionResponse } from '../models/DataFormActionResponse';
import { Menu } from '../models/Menu';
import { FormSerializer } from '../serializers/FormSerializer';
import { DataFormActionRequestSerializer } from '../serializers/DataFormActionRequestSerializer';
import { DataFormActionResponseSerializer } from '../serializers/DataFormActionResponseSerializer';
import { MenuSerializer } from '../serializers/MenuSerializer';

/**
 * HTTP client for Main operations
 *
 * Client for Main operations
 */
export class MainClient {
  private readonly config: ApiConfiguration;

  constructor(config: ApiConfiguration) {
    this.config = config;
  }

  async getFormForObject(objectType: string, formName: string, objectId: string, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/{objectType}({objectId})/{formName}/DataForm'
      .replace('{objectType}', encodeURIComponent(String(objectType)))
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async executeActionInObjectForm(objectType: string, formName: string, objectId: string, request?: DataFormActionRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/{objectType}({objectId})/{formName}/DataForm'
      .replace('{objectType}', encodeURIComponent(String(objectType)))
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(DataFormActionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async getFormForObjectType(objectType: string, formName: string, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/{objectType}/{formName}/DataForm'
      .replace('{objectType}', encodeURIComponent(String(objectType)))
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async executeActionInObjectTypeForm(objectType: string, formName: string, request?: DataFormActionRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/{objectType}/{formName}/DataForm'
      .replace('{objectType}', encodeURIComponent(String(objectType)))
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(DataFormActionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async getUserActionForm(objectType: string, objectId: string, eventId: string, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/{objectType}({objectId})/UserAction({eventId})/DataForm'
      .replace('{objectType}', encodeURIComponent(String(objectType)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      .replace('{eventId}', encodeURIComponent(String(eventId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async runUserAction(objectType: string, objectId: string, eventId: string, request?: DataFormActionRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/{objectType}({objectId})/UserAction({eventId})/DataForm'
      .replace('{objectType}', encodeURIComponent(String(objectType)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      .replace('{eventId}', encodeURIComponent(String(eventId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(DataFormActionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async getObjectMenu(objectType: string, objectId: string, options?: { headers?: Record<string, string> }): Promise<Menu> {
    let path = '/app/api/Object/{objectType}({objectId})/Menu'
      .replace('{objectType}', encodeURIComponent(String(objectType)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return MenuSerializer.fromObject(data);
  }

}
